import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Typography } from "@monolith-forensics/monolith-ui";
import { useTheme } from "styled-components";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardReturnOutlinedIcon from "@mui/icons-material/KeyboardReturnOutlined";
import styled from "styled-components";
import { usePermissions } from "@/hooks/usePermissions";
import Loader from "@/components/Loader";

import TaskButton from "@/Monolith-UI/TaskButton";

import ContactOverview from "@/Features/Contacts/ContactOverview";
import { DeleteContactModal } from "@/Features/Contacts/Modals/DeleteContactModal";
import { ContactProvider, useContactContext } from "@/Features";

const ContactHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 10px 0;
  justify-content: space-between;
`;

const ContactName = styled.div`
  margin: 0 10px;
  font-weight: bold;
  font-size: 20px;
`;

const BackLink = styled(({ message, ...props }) => {
  const theme: any = useTheme();
  let linkTo = `/contacts`;

  // Check if user clicked from inside a case, return to that case
  if (window.history?.state?.usr?.from?.includes("case:contacts")) {
    linkTo = `/cases/${window.history.state.usr.case_id}/contacts`;
  }

  return (
    <Link to={linkTo} {...props}>
      <TaskButton
        title={message}
        style={{ outline: `1px solid ${theme.palette.divider}` }}
      >
        <KeyboardReturnOutlinedIcon style={{ fontSize: 15 }} />
      </TaskButton>
    </Link>
  );
})`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: small;
  margin-left: auto;
  margin-bottom: 15px;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteButton = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  margin: 5px 10px;
  cursor: pointer;
  border-radius: 5px;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }

  svg {
    font-size: 18px;
    margin-right: 5px;
    padding-bottom: 2px;
  }
`;

const ContactDetailsContent = () => {
  const theme: any = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const contactContext = useContactContext();
  if (!contactContext) {
    throw new Error("Contact context not found");
  }

  const { data: contactInfo, isLoading, refetch } = contactContext;

  const handleContactUpdate = () => {
    refetch();
  };

  if (isLoading) return <Loader />;

  return (
    <>
      {contactInfo && (
        <>
          <Helmet>
            <title>Contact Details | {contactInfo.name}</title>
          </Helmet>
          <ContactHeader>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                minWidth: "fit-content",
              }}
            >
              <BackLink message={"Back to Contacts"} />
              <ContactName>
                {contactInfo.name || "Contact Details"}
                <Typography color="textSecondary" variant="subtitle2">
                  {contactInfo.organization || "No organization"}
                </Typography>
              </ContactName>
            </div>
            {hasPermission(MONOLITH_PERMISSIONS.CONTACTS_DELETE) && (
              <DeleteButton onClick={() => setShowDeleteModal(true)}>
                <DeleteOutlineOutlinedIcon
                  style={{
                    color: theme.palette.error.main,
                  }}
                />
                <div className="action-menu-label">Delete</div>
              </DeleteButton>
            )}
          </ContactHeader>
          <ContactOverview
            contactInfo={contactInfo}
            handleContactUpdate={handleContactUpdate}
          />
          <DeleteContactModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onCancel={() => setShowDeleteModal(false)}
            onSubmit={() => {}}
            defaultInfo={contactInfo}
          />
        </>
      )}
    </>
  );
};

export const ContactDetailsPage = () => {
  const { contact_id: contactIdString } = useParams();

  if (!contactIdString) {
    return <div>Contact not found</div>;
  }

  const contactId = parseInt(contactIdString);

  if (isNaN(contactId)) {
    return <div>Contact not found</div>;
  }

  return (
    <ContactProvider contact_id={contactId}>
      <ContactDetailsContent />
    </ContactProvider>
  );
};
